/**
 * The request-sent page (`/booked/:bookingId` for a request the studio has not
 * approved yet; approval-web.html Sent B): Request sent in the Telegram
 * message's words, the request's ticket, Add to Calendar and View my booking,
 * and Cancel request.
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'request-sent',
  description: "The request-sent page: Request sent, what happens next, the request's ticket, Add to Calendar, View my booking and Cancel request.",
  options: {},
  parts: {
    page: { description: 'The whole page.', required: false },
    title: { description: 'Request sent (Request cancelled once withdrawn).', required: true, role: 'title' },
    lead: { description: 'What happens next: the studio confirms and messages the client.', required: true },
    ticket: { description: "The request's ticket: the offering and its rows.", required: true },
    row: { description: 'One row of the ticket: Requested, Length, Where, the payment.', required: true },
    addToCalendar: { description: 'Add to Calendar, right of When: opens its menu.', required: false },
    calendarMenu: { description: "Add to Calendar's menu: Google Calendar, Outlook and Apple Calendar.", required: false },
    calendarGoogle: { description: 'Google Calendar: opens it with the booking filled in.', required: false },
    calendarOutlook: { description: 'Outlook: opens Outlook on the web with the booking filled in.', required: false },
    calendarApple: { description: 'Apple Calendar: downloads the calendar file.', required: false },
    viewBooking: { description: 'View my booking.', required: false, role: 'primary-action' },
    cancel: { description: 'Cancel request.', required: true },
    problem: { description: 'Why the request could not be cancelled.', required: true },
    telegramLine: { description: 'Open chat, when Telegram would not deliver the request message.', required: true },
  },
  actions: {
    addToCalendar: { description: "Downloads the request as a calendar file (the menu's Apple Calendar).", args: [] },
    cancel: { description: 'Withdraws the request.', args: [] },
  },
  events: {
    cancelled: 'The client withdrew the request.',
  },
});

/** What the request-sent page knows. */
export interface RequestSentState {
  /** loading until the request is read; not-found when it is not this client's. */
  readonly status: 'loading' | 'ready' | 'not-found';
  /** True once the request was withdrawn (or declined). */
  readonly cancelled: boolean;
  /** The lead under the title. */
  readonly lead: string;
  /** The offering's name. */
  readonly name: string;
  /** Its photo, or null. */
  readonly photoUrl: string | null;
  /** website-photo-zoom: the same photo framed as the studio set it (crop point and zoom), or null. */
  readonly photo: SitePhoto | null;
  /** "with <name>", or null. */
  readonly withName: string | null;
  /** The ticket's rows. */
  readonly rows: readonly {
    readonly label: string;
    readonly value: string;
    readonly sub: string | null;
    readonly amount: string | null;
    readonly directionsHref: string | null;
  }[];
  /** True while Cancel request is offered. */
  readonly canCancel: boolean;
  /** True while the request is being withdrawn. */
  readonly cancelling: boolean;
  /** Why it could not be withdrawn; null otherwise. */
  readonly problem: string | null;
  /** Add to Calendar's Google Calendar and Outlook links, or null before the booking is read. */
  readonly calendar: { readonly google: string; readonly outlook: string } | null;
  /** Open chat when Telegram would not deliver the request message; null otherwise. */
  readonly telegram: { readonly kind: 'sent' | 'refused'; readonly text: string; readonly chatHref: string | null } | null;
  /** Where View my booking goes. */
  readonly bookingHref: string;
}

/** What a drawing can ask the request-sent page to do. */
export interface RequestSentActions {
  /** Download the request as a calendar file. */
  readonly addToCalendar: () => void;
  /** Withdraw the request. */
  readonly cancel: () => void;
}

/** What the request-sent page tells its host. */
export interface RequestSentEvents {
  /** The client withdrew the request. */
  readonly cancelled: Record<string, never>;
}
